/**
 * @file 资源管理页（Resources）
 *
 * 该文件定义了 CRM 系统的资源管理组件 `CrmResourcesPage`。
 * 主要功能包括：
 * - 资源列表展示（表格形式，含描述、网址、备注、分类、创建时间）
 * - 按关键词搜索（防抖 300ms）和按分类筛选
 * - 新增/编辑资源（模态框表单，含描述、网址、备注、分类）
 * - 删除资源（带确认对话框，删除最后一项时自动回退到上一页）
 * - 分类管理（独立的模态框，支持分类的增删改查）
 * - 分页导航
 *
 * 数据来源：`resourceApi`（资源 CRUD）和 `resourceCategoryApi`（分类 CRUD）
 *
 * 设计决策：
 * - 关键词搜索使用防抖（300ms）避免频繁请求
 * - 分类筛选和资源列表共用加载方法
 * - 删除资源时如果当前页只剩一条，自动回退到上一页
 * - 模态框背景点击关闭（mousedown + click 模式避免误触）
 * - 时间戳格式化为本地日期时间字符串
 * - 长文本截断显示（描述截断 80 字符，网址 60 字符，备注 60 字符）
 */

import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { resourceApi, resourceCategoryApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { Resource, ResourceCategory } from '../types.js';
import { toast } from '../components/toast.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';
import '../components/select.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/pagination.js';

/**
 * 将 Unix 时间戳格式化为本地日期时间字符串
 * @param ts - Unix 时间戳（秒）
 * @returns 格式化的日期时间字符串，格式为 YYYY-MM-DD HH:mm
 */
function formatTimestamp(ts: number): string {
  const d = new Date(ts * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 截断字符串并添加省略号
 * @param s - 原始字符串
 * @param n - 最大保留长度
 * @returns 截断后的字符串（超出长度时末尾添加「...」），空字符串原样返回
 */
function truncate(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '...' : s;
}

/**
 * 资源管理页组件
 *
 * 继承 `BaseElement`，注册为 `<crm-resources-page>` 自定义元素。
 * 提供资源的增删改查和分类管理功能。
 *
 * 状态字段分为三组：
 * - 资源列表：items / isLoading / total / currentPage / perPage
 * - 筛选条件：filterKeyword / filterCategoryId
 * - 资源表单：showModal / editingId / isSaving / formDescription / formUrl / formNotes / formCategoryId
 * - 分类表单：showCategoryModal / editingCategoryId / categoryFormName
 */
@customElement('crm-resources-page')
export class CrmResourcesPage extends BaseElement {
  /** 资源列表数据 */
  @state() private items: Resource[] = [];
  /** 分类列表数据 */
  @state() private categories: ResourceCategory[] = [];
  /** 加载状态标志 */
  @state() private isLoading = false;
  /** 列表请求序号，仅允许最后一次筛选请求更新页面。 */
  private _listRequestId = 0;
  /** 资源总数，用于分页 */
  @state() private total = 0;
  /** 当前页码 */
  @state() private currentPage = 1;
  /** 每页条数 */
  @state() private perPage = 20;

  /** 关键词搜索值 */
  @state() private filterKeyword = '';
  /** 分类筛选 ID，null 表示所有分类 */
  @state() private filterCategoryId: number | null = null;
  /** 搜索防抖计时器 ID */
  private _searchTimer: number | null = null;

  /** 是否显示资源新增/编辑模态框 */
  @state() private showModal = false;
  /** 当前编辑的资源 ID，0 表示新增模式 */
  @state() private editingId = 0;
  /** 保存中状态，防止重复提交 */
  @state() private isSaving = false;
  /** 表单 - 资源描述 */
  @state() private formDescription = '';
  /** 表单 - 资源网址 */
  @state() private formUrl = '';
  /** 表单 - 资源备注 */
  @state() private formNotes = '';
  /** 表单 - 资源分类 ID，0 表示无分类 */
  @state() private formCategoryId = 0;

  /** 是否显示分类管理模态框 */
  @state() private showCategoryModal = false;
  /** 当前编辑的分类 ID，0 表示新增模式 */
  @state() private editingCategoryId = 0;
  /** 分类表单 - 分类名称 */
  @state() private categoryFormName = '';

  /** 模态框背景点击检测标志，避免拖拽选中文本时误触发关闭 */
  private _backdropMousedown = false;

  /**
   * 组件挂载到 DOM 后的生命周期回调
   *
   * 并行加载资源列表和分类列表。
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadResources();
    this.loadCategories();
  }

  /**
   * 组件从 DOM 移除时的生命周期回调
   *
   * 清除搜索防抖计时器，防止组件销毁后仍触发请求。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  /**
   * 加载资源列表
   *
   * 根据当前分页、关键词和分类筛选条件调用 `resourceApi.list()` 获取数据。
   * 成功后更新资源列表和总数。失败时显示错误 toast（过滤网络错误）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async loadResources() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      // 构建查询参数，仅添加有值的筛选条件
      const params: { page: number; per_page: number; keyword?: string; category_id?: number } = {
        page: this.currentPage,
        per_page: this.perPage,
      };
      if (this.filterKeyword) params.keyword = this.filterKeyword;
      if (this.filterCategoryId !== null) params.category_id = this.filterCategoryId;
      const r = await resourceApi.list(params);
      if (requestId !== this._listRequestId) return;
      this.items = r.data;
      this.total = r.pagination.total;
    } catch (err: any) {
      if (requestId !== this._listRequestId) return;
      const msg = err instanceof Error ? err.message : String(err);
      if (isNetworkError(msg)) return;
      toast.error(msg || '加载失败');
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 加载分类列表
   *
   * 调用 `resourceCategoryApi.list()` 获取所有分类，用于筛选和表单下拉。
   * 失败时显示错误 toast（过滤网络错误）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async loadCategories() {
    try {
      const r = await resourceCategoryApi.list();
      this.categories = r.data;
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!isNetworkError(msg)) toast.error(msg || '加载分类失败');
    }
  }

  /**
   * 搜索输入处理器（防抖 300ms）
   *
   * 用户输入时清除之前的计时器，300ms 后如果无新输入则触发搜索。
   * 搜索时重置到第一页。
   *
   * @param value - 搜索关键词
   */
  private handleSearchInput(value: string) {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = window.setTimeout(() => {
      this.filterKeyword = value;
      this.currentPage = 1;
      this.loadResources();
    }, 300);
  }

  /**
   * 分类筛选处理器
   *
   * 设置分类筛选条件并重置到第一页后重新加载资源列表。
   * @param categoryId - 分类 ID，null 表示所有分类
   */
  private handleCategoryFilter(categoryId: number | null) {
    this.filterCategoryId = categoryId;
    this.currentPage = 1;
    this.loadResources();
  }

  /**
   * 打开资源新增/编辑模态框
   *
   * 不传 ID 时为新增模式，传入 ID 时从当前列表查找对应资源并预填表单。
   * @param id - 可选的资源 ID，传入时为编辑模式
   */
  private openModal(id?: number) {
    this.editingId = id || 0;
    if (id) {
      // 编辑模式：从当前列表查找资源并预填表单字段
      const item = this.items.find((i) => i.id === id);
      if (item) {
        this.formDescription = item.description;
        this.formUrl = item.url;
        this.formNotes = item.notes;
        this.formCategoryId = item.category_id;
      }
    } else {
      // 新增模式：清空表单字段
      this.formDescription = '';
      this.formUrl = '';
      this.formNotes = '';
      this.formCategoryId = 0;
    }
    this.showModal = true;
  }

  /**
   * 关闭资源模态框并重置表单状态
   */
  private closeModal() {
    this.showModal = false;
    this.editingId = 0;
    this.formDescription = '';
    this.formUrl = '';
    this.formNotes = '';
    this.formCategoryId = 0;
  }

  /**
   * 资源表单提交处理器
   *
   * 校验描述非空后，根据编辑/新增模式分别调用 update/create API。
   * 成功后重新加载列表并关闭模态框。
   * 使用 isSaving 标志防止重复提交。
   *
   * @param e - 表单提交事件
   * @returns 无返回值（Promise<void>）
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    // 防止保存过程中重复提交
    if (this.isSaving) return;
    const description = this.formDescription.trim();
    if (!description) {
      toast.warning('请输入描述');
      return;
    }
    const payload = {
      description,
      url: this.formUrl.trim(),
      notes: this.formNotes,
      category_id: this.formCategoryId,
    };
    this.isSaving = true;
    try {
      if (this.editingId) {
        await resourceApi.update(this.editingId, payload);
        toast.success('更新成功');
      } else {
        await resourceApi.create(payload);
        toast.success('创建成功');
      }
      await this.loadResources();
      this.closeModal();
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * 删除资源
   *
   * 弹出确认对话框，用户确认后调用 `resourceApi.delete()` 删除。
   * 如果当前页只剩一条数据且不在第一页，自动回退到上一页后重新加载。
   *
   * @param id - 待删除的资源 ID
   * @returns 无返回值（Promise<void>）
   */
  private async handleDelete(id: number) {
    const ok = await showConfirm('确定要删除这条资源吗？');
    if (!ok) return;
    try {
      await resourceApi.delete(id);
      toast.success('删除成功');
      // 删除最后一项时自动回退到上一页，避免空白页
      if (this.items.length === 1 && this.currentPage > 1) {
        this.currentPage--;
      }
      await this.loadResources();
    } catch (err: any) {
      toast.error(err.message || '删除失败');
    }
  }

  // ========== 分类管理 ==========

  /**
   * 提交分类表单（新增或更新）
   *
   * 根据 editingCategoryId 判断是新增还是更新分类。
   * 成功后清空表单并重新加载分类列表。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async submitCategory() {
    const name = this.categoryFormName.trim();
    if (!name) return;
    try {
      if (this.editingCategoryId) {
        await resourceCategoryApi.update(this.editingCategoryId, name);
        toast.success('分类更新成功');
      } else {
        await resourceCategoryApi.create({ name });
        toast.success('分类添加成功');
      }
      this.editingCategoryId = 0;
      this.categoryFormName = '';
      await this.loadCategories();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '操作失败');
    }
  }

  /**
   * 删除分类
   *
   * 弹出确认对话框（提示资源不会被删除但会显示为未分类）。
   * 如果当前筛选的就是被删除的分类，则清空筛选条件。
   * 成功后重新加载分类列表和资源列表。
   *
   * @param c - 待删除的分类对象
   * @returns 无返回值（Promise<void>）
   */
  private async deleteCategory(c: ResourceCategory) {
    if (!(await showConfirm(`删除分类「${c.name}」？该分类下的资源不会被删除，但会显示为「未分类」。`))) return;
    try {
      await resourceCategoryApi.delete(c.id);
      // 如果当前筛选的就是被删除的分类，清空筛选条件
      if (this.filterCategoryId === c.id) this.filterCategoryId = null;
      await this.loadCategories();
      await this.loadResources();
      toast.success('分类已删除');
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  /**
   * 渲染分类管理模态框
   *
   * 包含分类输入框、添加/保存按钮和已有分类列表（含编辑和删除操作）。
   * 全局分类会标注「全局」标签。
   * 模态框未显示时返回空字符串。
   *
   * @returns Lit TemplateResult 或空字符串
   */
  private renderCategoryModal() {
    if (!this.showCategoryModal) return '';
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50"
           @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }}
           @click=${() => { if (this._backdropMousedown) { this.showCategoryModal = false; this.editingCategoryId = 0; this.categoryFormName = ''; } }}>
        <div class="bg-surface rounded-md shadow-xl w-full max-w-2xl border border-border mx-4" @click=${(e: Event) => e.stopPropagation()}>
          <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
            <h2 class="m-0 font-semibold text-lg text-text">分类管理</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors"
                    @click=${() => { this.showCategoryModal = false; this.editingCategoryId = 0; this.categoryFormName = ''; }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="p-6 space-y-3">
            <div class="flex gap-2">
              <input type="text" .value=${this.categoryFormName}
                     @input=${(e: Event) => { this.categoryFormName = (e.target as HTMLInputElement).value; }}
                     placeholder=${this.editingCategoryId ? '修改分类名称' : '输入分类名称'}
                     class="flex-1 px-3 py-2 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none" />
              <button class="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-md transition-colors cursor-pointer"
                      @click=${this.submitCategory}>
                ${this.editingCategoryId ? '保存' : '添加'}
              </button>
              ${this.editingCategoryId ? html`
                <button class="px-4 py-2 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded-md transition-colors cursor-pointer"
                        @click=${() => { this.editingCategoryId = 0; this.categoryFormName = ''; }}>取消</button>
              ` : ''}
            </div>
            <div class="space-y-1.5 max-h-[480px] overflow-y-auto">
              ${this.categories.length === 0 ? html`<div class="text-sm text-text-muted text-center py-4">暂无分类</div>` :
                this.categories.map(c => html`
                  <div class="flex items-center justify-between px-3 py-2 rounded border border-border bg-bg">
                    <span class="text-sm text-text">${c.name}${c.is_global ? html`<span class="ml-1 text-xs text-text-muted">（全局）</span>` : ''}</span>
                    <div class="flex gap-1">
                      <button class="px-2 py-0.5 text-xs text-primary hover:bg-primary/10 rounded transition-colors cursor-pointer"
                              @click=${() => { this.editingCategoryId = c.id; this.categoryFormName = c.name; }}>编辑</button>
                      <button class="px-2 py-0.5 text-xs text-error hover:bg-error/10 rounded transition-colors cursor-pointer"
                              @click=${() => this.deleteCategory(c)}>删除</button>
                    </div>
                  </div>
                `)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染资源管理页面
   *
   * 页面布局：
   * 1. 加载遮罩
   * 2. 顶部工具栏：搜索框、分类筛选下拉、分类管理按钮、新建资源按钮、刷新/计数工具栏
   * 3. 资源表格：描述、网址、备注、分类、创建时间、操作（编辑/删除）
   * 4. 分页组件
   * 5. 资源新增/编辑模态框（条件渲染）
   * 6. 分类管理模态框（条件渲染）
   *
   * 分类选项动态生成：筛选下拉含「全部分类」选项，表单下拉含「无分类」选项。
   * 全局分类在选项标签后添加「（全局）」标注。
   *
   * @returns Lit TemplateResult
   */
  render() {
    // 构建筛选下拉选项：全部分类 + 所有已有分类
    const categoryOptions = [
      { label: '全部分类', value: '' },
      ...this.categories.map(c => ({
        label: c.is_global ? `${c.name}（全局）` : c.name,
        value: String(c.id),
      })),
    ];
    // 构建表单下拉选项：无分类 + 所有已有分类
    const formCategoryOptions = [
      { label: '无分类', value: '0' },
      ...this.categories.map(c => ({
        label: c.is_global ? `${c.name}（全局）` : c.name,
        value: String(c.id),
      })),
    ];

    return html`
      <crm-loading-overlay ?active=${this.isLoading}></crm-loading-overlay>

      <div class="mb-2 flex items-center gap-3 flex-wrap">
        <input type="text" placeholder="搜索描述/网址/备注..."
               .value=${this.filterKeyword}
               @input=${(e: Event) => this.handleSearchInput((e.target as HTMLInputElement).value)}
               class="px-3 py-1.5 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none w-64" />

        <crm-select
          style="min-width: 160px"
          .value=${this.filterCategoryId === null ? '' : String(this.filterCategoryId)}
          .options=${categoryOptions}
          @change=${(e: CustomEvent) => {
            const v = e.detail.value;
            this.handleCategoryFilter(v === '' ? null : Number(v));
          }}></crm-select>

        <button class="px-3 py-1.5 text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 border border-primary/30 rounded transition-colors cursor-pointer"
                @click=${() => { this.showCategoryModal = true; }}>
          分类管理
        </button>

        <div class="flex-1"></div>

        <button class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-md transition-colors cursor-pointer shadow-sm"
                @click=${() => this.openModal()}>
          + 新建资源
        </button>

        <crm-table-toolbar
          .total=${this.total}
          .label=${'条资源'}
          .loading=${this.isLoading}
          @crm-toolbar-refresh=${() => this.loadResources()}
        ></crm-table-toolbar>
      </div>

      <div class="bg-surface rounded-lg border border-border overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-border bg-white text-left">
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">描述</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">网址</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">备注</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-32">分类</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-40">创建时间</th>
                <th class="px-4 py-2.5 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider w-32 whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody>
              ${this.items.length === 0
                ? html`<tr><td colspan="6" class="px-4 py-12 text-center text-text-muted text-sm">暂无数据</td></tr>`
                : this.items.map(item => html`
                  <tr class="border-b border-border hover:bg-bg/50 transition-colors">
                    <td class="px-4 py-3 text-sm text-text">${truncate(item.description, 80)}</td>
                    <td class="px-4 py-3 text-sm text-text-secondary break-all">
                      ${item.url ? truncate(item.url, 60) : html`<span class="text-text-muted">—</span>`}
                    </td>
                    <td class="px-4 py-3 text-sm text-text-secondary">${truncate(item.notes, 60)}</td>
                    <td class="px-4 py-3 text-sm text-text-secondary">${item.category_name || '未分类'}</td>
                    <td class="px-4 py-3 text-sm text-text-secondary">${formatTimestamp(item.created_at)}</td>
                    <td class="px-4 py-3 whitespace-nowrap">
                      <div class="flex gap-1">
                        <button class="px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10 rounded transition-colors cursor-pointer"
                                @click=${() => this.openModal(item.id)}>编辑</button>
                        <button class="px-2.5 py-1 text-xs font-medium text-error hover:bg-error/10 rounded transition-colors cursor-pointer"
                                @click=${() => this.handleDelete(item.id)}>删除</button>
                      </div>
                    </td>
                  </tr>
                `)}
            </tbody>
          </table>
        </div>
        <crm-pagination
          .current=${this.currentPage}
          .perPage=${this.perPage}
          .total=${this.total}
          @page-change=${(e: CustomEvent) => { this.currentPage = e.detail.page; this.loadResources(); }}
        ></crm-pagination>
      </div>

      ${this.showModal ? html`
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-start z-50 overflow-y-auto p-4"
             @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }}
             @click=${() => { if (this._backdropMousedown) this.closeModal(); }}>
          <div class="bg-surface rounded-lg shadow-xl w-full max-w-2xl border border-border my-8" @click=${(e: Event) => e.stopPropagation()}>
            <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
              <h2 class="text-lg font-semibold text-text">${this.editingId ? '编辑资源' : '新建资源'}</h2>
              <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <form @submit=${this.handleSubmit} class="p-6 space-y-4">
              <div>
                <label class="block mb-1.5 text-sm font-medium text-text">描述 <span class="text-error">*</span></label>
                <textarea .value=${this.formDescription}
                          @input=${(e: Event) => { this.formDescription = (e.target as HTMLTextAreaElement).value; }}
                          rows="2" required maxlength="500"
                          class="w-full px-3.5 py-1.5 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none resize-y"
                          placeholder="例如：Lit 官方文档"></textarea>
              </div>
              <div>
                <label class="block mb-1.5 text-sm font-medium text-text">网址</label>
                <input type="text" .value=${this.formUrl}
                       @input=${(e: Event) => { this.formUrl = (e.target as HTMLInputElement).value; }}
                       maxlength="2048"
                       class="w-full px-3.5 py-1.5 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                       placeholder="https://..." />
              </div>
              <div>
                <label class="block mb-1.5 text-sm font-medium text-text">备注</label>
                <textarea .value=${this.formNotes}
                          @input=${(e: Event) => { this.formNotes = (e.target as HTMLTextAreaElement).value; }}
                          rows="3"
                          class="w-full px-3.5 py-1.5 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none resize-y"
                          placeholder="可选"></textarea>
              </div>
              <div>
                <label class="block mb-1.5 text-sm font-medium text-text">分类</label>
                <crm-select fullWidth
                  .value=${String(this.formCategoryId)}
                  .options=${formCategoryOptions}
                  @change=${(e: CustomEvent) => { this.formCategoryId = Number(e.detail.value); }}></crm-select>
              </div>
              <div class="flex justify-end gap-2 pt-2">
                <button type="button"
                        ?disabled=${this.isSaving}
                        class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded-md transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        @click=${this.closeModal}>取消</button>
                <button type="submit"
                        ?disabled=${this.isSaving}
                        class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-md transition-colors cursor-pointer shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">${this.isSaving ? '保存中...' : '保存'}</button>
              </div>
            </form>
          </div>
        </div>
      ` : ''}

      ${this.renderCategoryModal()}
    `;
  }
}
